import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaSearch, FaSpinner, FaUser, FaUserPlus, FaUserCheck, FaHeart, FaComment, FaShare, FaUsers, FaPlay } from 'react-icons/fa';
import { searchAPI, usersAPI } from '../services/api';
import AppBar from '../components/AppBar';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
`;

const SearchContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 48px 16px 16px;
  background-color: #111;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #666;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 18px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  color: #666;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const EmptyStateText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
`;

const EmptyStateSubtext = styled.div`
  font-size: 16px;
  color: #ccc;
`;

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserCard = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #333;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  a {
    text-decoration: none;
    color: inherit;
  }
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const UserAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

const UserUsername = styled.div`
  color: #666;
  font-size: 14px;
`;

const UserBio = styled.div`
  color: #ccc;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 16px;
`;

const UserStats = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const Stat = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 14px;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 12px;
`;

const FollowButton = styled.button`
  background-color: ${props => props.isFollowing ? '#333' : '#4CAF50'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;

  &:hover:not(:disabled) {
    background-color: ${props => props.isFollowing ? '#444' : '#45a049'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
  margin-top: 16px;
  padding: 0 8px;
`;

const PostCard = styled.div`
  position: relative;
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
  aspect-ratio: 9/16;
  min-height: 400px;

  &:hover {
    transform: scale(1.02);
  }
`;

const PostMedia = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const PostOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 20px 12px 12px;
  z-index: 2;
`;

const PostUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const PostAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
`;

const PostUserName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const PostContent = styled.div`
  color: #fff;
  font-size: 13px;
  line-height: 1.3;
  margin-bottom: 12px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #fff;
  font-size: 12px;
`;

const PostAction = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const PostPlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${PostCard}:hover & {
    opacity: 1;
  }
`;

const PostDuration = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  z-index: 3;
`;

const GroupsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
  margin-top: 16px;
  padding: 0 8px;
`;

const GroupCard = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 16px;
  border: none;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #4CAF50, #45a049);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const GroupAvatar = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  object-fit: cover;
  border: 2px solid #333;
  transition: border-color 0.2s ease;

  ${GroupCard}:hover & {
    border-color: #4CAF50;
  }
`;

const GroupInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const GroupName = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 6px;
  line-height: 1.2;
`;

const GroupDescription = styled.div`
  color: #ccc;
  font-size: 14px;
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const GroupStats = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  color: #888;
  font-size: 13px;
  margin-bottom: 16px;
`;

const GroupStat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
`;

const JoinButton = styled.button`
  background-color: ${props => props.isMember ? '#666' : '#4CAF50'};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover:not(:disabled) {
    background-color: ${props => props.isMember ? '#666' : '#45a049'};
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const SearchScreen = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('users');
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [following, setFollowing] = useState(new Set());

  const searchContent = async (query) => {
    if (!query.trim()) {
      setUsers([]);
      setPosts([]);
      setGroups([]);
      return;
    }

    setLoading(true);
    try {
      const response = await searchAPI.search(query, searchType);
      console.log('🔍 Search results:', response);
      console.log('🔍 Response type:', typeof response);
      console.log('🔍 Response keys:', Object.keys(response || {}));
      
      // Handle different response structures based on search type
      if (searchType === 'users') {
        const usersArray = Array.isArray(response) ? response : (response?.users || []);
        console.log('🔍 Final users array:', usersArray);
        setUsers(usersArray);
        setPosts([]);
        setGroups([]);
      } else if (searchType === 'posts') {
        const postsArray = Array.isArray(response) ? response : (response?.posts || []);
        console.log('🔍 Final posts array:', postsArray);
        setPosts(postsArray);
        setUsers([]);
        setGroups([]);
      } else if (searchType === 'groups') {
        const groupsArray = Array.isArray(response) ? response : (response?.groups || []);
        console.log('🔍 Final groups array:', groupsArray);
        setGroups(groupsArray);
        setUsers([]);
        setPosts([]);
      }
    } catch (error) {
      console.error('Error searching content:', error);
      toast.error('Failed to search content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchContent(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  const handleFollow = async (userId) => {
    try {
      if (following.has(userId)) {
        await usersAPI.unfollowUser(userId);
        setFollowing(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        toast.success('Unfollowed user');
      } else {
        await usersAPI.followUser(userId);
        setFollowing(prev => new Set([...prev, userId]));
        toast.success('Followed user');
      }
    } catch (error) {
      console.error('Error following/unfollowing user:', error);
      toast.error('Failed to update follow status');
    }
  };

  const handleLikePost = async (postId) => {
    // This would need to be implemented with the posts API
    toast.success('Post liked!');
  };

  const handleJoinGroup = async (groupId) => {
    // This would need to be implemented with the groups API
    toast.success('Joined group!');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Container>
      <AppBar />
      <SearchContainer>
        <Header>
          <Title>Search</Title>
          
          {/* Search Type Filters */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setSearchType('users')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: searchType === 'users' ? '#4CAF50' : '#222',
                  color: searchType === 'users' ? '#fff' : '#ccc'
                }}
              >
                Users
              </button>
              <button
                onClick={() => setSearchType('posts')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: searchType === 'posts' ? '#4CAF50' : '#222',
                  color: searchType === 'posts' ? '#fff' : '#ccc'
                }}
              >
                Posts
              </button>
              <button
                onClick={() => setSearchType('groups')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: searchType === 'groups' ? '#4CAF50' : '#222',
                  color: searchType === 'groups' ? '#fff' : '#ccc'
                }}
              >
                Groups
              </button>
            </div>
          </div>
          
          <SearchInputContainer>
            <SearchInput
              type="text"
              placeholder={`Search ${searchType}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchIcon>
              <FaSearch />
            </SearchIcon>
          </SearchInputContainer>
        </Header>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner>
              <FaSpinner />
              Searching users...
            </LoadingSpinner>
          </LoadingContainer>
        ) : searchQuery && users.length === 0 && posts.length === 0 && groups.length === 0 ? (
          <EmptyState>
            <EmptyStateText>No {searchType} found</EmptyStateText>
            <EmptyStateSubtext>Try searching with a different term</EmptyStateSubtext>
          </EmptyState>
        ) : !searchQuery ? (
          <EmptyState>
            <EmptyStateText>Start searching</EmptyStateText>
            <EmptyStateSubtext>Enter a term to find {searchType}</EmptyStateSubtext>
          </EmptyState>
        ) : (
          <>
            {console.log('🎨 Rendering content:', { users, posts, groups, searchType })}
            
            {/* Users Results */}
            {searchType === 'users' && users.length > 0 && (
              <UsersGrid>
                {users.map((user) => (
              <UserCard key={user.id}>
                <Link to={`/user/${user.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <UserHeader>
                    <UserAvatar
                      src={user.meta?.profile_image || '/default-avatar.png'}
                      alt={user.meta?.name || user.username}
                    />
                    <UserInfo>
                      <UserName>
                        {user.meta?.name || user.username || 'Unknown User'}
                      </UserName>
                      <UserUsername>@{user.username}</UserUsername>
                    </UserInfo>
                  </UserHeader>
                </Link>

                {user.meta?.bio && <UserBio>{user.meta.bio}</UserBio>}

                <UserStats>
                  <Stat>
                    <StatNumber>{user.stats?.posts || 0}</StatNumber>
                    <StatLabel>Posts</StatLabel>
                  </Stat>
                  <Stat>
                    <StatNumber>{user.stats?.followers || 0}</StatNumber>
                    <StatLabel>Followers</StatLabel>
                  </Stat>
                  <Stat>
                    <StatNumber>{user.stats?.followings || 0}</StatNumber>
                    <StatLabel>Following</StatLabel>
                  </Stat>
                </UserStats>

                <FollowButton
                  isFollowing={following.has(user.id)}
                  onClick={() => handleFollow(user.id)}
                >
                  {following.has(user.id) ? (
                    <>
                      <FaUserCheck />
                      Following
                    </>
                  ) : (
                    <>
                      <FaUserPlus />
                      Follow
                    </>
                  )}
                </FollowButton>
              </UserCard>
            ))}
          </UsersGrid>
            )}
            
            {/* Posts Results */}
            {searchType === 'posts' && posts.length > 0 && (
              <PostsGrid>
                {posts.map((post) => (
                  <PostCard key={post.id}>
                    {/* Media Background */}
                    {post.media && post.media.length > 0 && (
                      <PostMedia
                        src={post.media[0].path}
                        alt="Post media"
                      />
                    )}
                    
                    {/* Play Button for Videos */}
                    {post.media && post.media.length > 0 && post.media[0].path?.includes('.m3u8') && (
                      <PostPlayButton>
                        <FaPlay style={{ color: '#000', fontSize: '20px' }} />
                      </PostPlayButton>
                    )}
                    
                    {/* Duration Badge for Videos */}
                    {post.media && post.media.length > 0 && post.media[0].duration && (
                      <PostDuration>
                        {Math.floor(post.media[0].duration / 60)}:{(post.media[0].duration % 60).toString().padStart(2, '0')}
                      </PostDuration>
                    )}
                    
                    {/* Overlay with User Info and Actions */}
                    <PostOverlay>
                      <PostUserInfo>
                        <PostAvatar
                          src={post.user?.meta?.profile_image || '/default-avatar.png'}
                          alt={post.user?.meta?.name || post.user?.username}
                        />
                        <PostUserName>
                          @{post.user?.username || 'unknown'}
                        </PostUserName>
                      </PostUserInfo>
                      
                      {post.content && (
                        <PostContent>{post.content}</PostContent>
                      )}
                      
                      <PostActions>
                        <PostAction onClick={() => handleLikePost(post.id)}>
                          <FaHeart />
                          {post.likes_count || 0}
                        </PostAction>
                        <PostAction>
                          <FaComment />
                          {post.comments_count || 0}
                        </PostAction>
                        <PostAction>
                          <FaShare />
                          Share
                        </PostAction>
                      </PostActions>
                    </PostOverlay>
                  </PostCard>
                ))}
              </PostsGrid>
            )}
            
            {/* Groups Results */}
            {searchType === 'groups' && groups.length > 0 && (
              <GroupsGrid>
                {groups.map((group) => (
                  <GroupCard key={group.id}>
                    <GroupHeader>
                      <GroupAvatar
                        src={group.avatar || group.cover_image || '/default-avatar.png'}
                        alt={group.name}
                      />
                      <GroupInfo>
                        <GroupName>{group.name}</GroupName>
                        <GroupDescription>{group.description}</GroupDescription>
                        <GroupStats>
                          <GroupStat>
                            <FaUsers />
                            {group.members_count || 0} members
                          </GroupStat>
                          <GroupStat>
                            {group.posts_count || 0} posts
                          </GroupStat>
                        </GroupStats>
                      </GroupInfo>
                    </GroupHeader>
                    
                    <JoinButton
                      onClick={() => handleJoinGroup(group.id)}
                      disabled={group.is_member}
                      isMember={group.is_member}
                    >
                      {group.is_member ? '✓ Joined' : 'Join Group'}
                    </JoinButton>
                  </GroupCard>
                ))}
              </GroupsGrid>
            )}
          </>
        )}
      </SearchContainer>
    </Container>
  );
};

export default SearchScreen; 